import { useEffect, useState, useCallback } from 'react'
import { PageHeader, DataTable, StatusBadge } from '../../components/ui'
import type { Column } from '../../components/ui'
import { colors, btnPrimary } from '../../components/ui/styles'
import { api } from '../../services/api'
import { useBackupSnapshots, useInitiateRestore, useRestoreJob } from '../../hooks/use-backups'
import type { BackupSnapshot } from '../../hooks/use-backups'
import { relativeTime } from './fleet-helpers'

interface EnvironmentBackupSummary {
  readonly id: string
  readonly name: string
  readonly backupTier: string
  readonly backupPassphraseEnabled: boolean
  readonly lastBackupAt: string | null
  readonly snapshotCount: number
  readonly totalSizeBytes: number
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

function backupAgeColor(lastAt: string | null): string {
  if (!lastAt) return colors.danger
  const hoursAgo = (Date.now() - new Date(lastAt).getTime()) / 3_600_000
  if (hoursAgo < 24) return colors.success
  if (hoursAgo < 36) return colors.warning
  return colors.danger
}

function backupAgeLabel(lastAt: string | null): string {
  if (!lastAt) return 'Never'
  return relativeTime(lastAt)
}

function countStaleEnvironments(envs: readonly EnvironmentBackupSummary[]): number {
  const now = Date.now()
  return envs.filter((e) => {
    if (!e.lastBackupAt) return true
    return (now - new Date(e.lastBackupAt).getTime()) / 3_600_000 > 36
  }).length
}

export function BackupDashboardPage() {
  const [environments, setEnvironments] = useState<readonly EnvironmentBackupSummary[]>([])
  const [_loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedEnvId, setExpandedEnvId] = useState<string | null>(null)
  const [restoreDialogSnapshot, setRestoreDialogSnapshot] = useState<BackupSnapshot | null>(null)
  const [restorePassphrase, setRestorePassphrase] = useState('')
  const [activeJobId, setActiveJobId] = useState<string | null>(null)

  const { data: snapshots } = useBackupSnapshots(expandedEnvId)
  const { initiate, loading: restoring, error: restoreError } = useInitiateRestore()
  const { data: activeJob } = useRestoreJob(activeJobId)

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly EnvironmentBackupSummary[]>('/admin/backups/summary')
      .then((res) => {
        if (cancelled) return
        setEnvironments(res.data ?? [])
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load backup summary')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const staleCount = countStaleEnvironments(environments)

  const handleRestore = useCallback(async () => {
    if (!restoreDialogSnapshot) return
    try {
      const job = await initiate(restoreDialogSnapshot.id, {
        passphrase: restorePassphrase || undefined,
      })
      if (job) setActiveJobId(job.id)
      setRestoreDialogSnapshot(null)
      setRestorePassphrase('')
    } catch {
      // error surfaced via restoreError
    }
  }, [restoreDialogSnapshot, restorePassphrase, initiate])

  const envColumns: Column<EnvironmentBackupSummary>[] = [
    { key: 'name', header: 'Environment', render: (r) => r.name },
    {
      key: 'tier',
      header: 'Tier',
      render: (r) => <StatusBadge status={r.backupTier === 'continuous' ? 'active' : 'info'} label={r.backupTier} />,
    },
    {
      key: 'lastBackup',
      header: 'Last Backup',
      render: (r) => <span style={{ color: backupAgeColor(r.lastBackupAt) }}>{backupAgeLabel(r.lastBackupAt)}</span>,
    },
    { key: 'snapshots', header: 'Snapshots', render: (r) => <>{r.snapshotCount}</> },
    { key: 'totalSize', header: 'Total Size', render: (r) => <>{formatBytes(r.totalSizeBytes)}</> },
    {
      key: 'passphrase',
      header: 'Passphrase',
      render: (r) => <>{r.backupPassphraseEnabled ? 'Enabled' : '-'}</>,
    },
  ]

  const snapshotColumns: Column<BackupSnapshot>[] = [
    { key: 'uploadedAt', header: 'Uploaded', render: (r) => <>{new Date(r.uploadedAt).toLocaleString()}</> },
    { key: 'snapshotType', header: 'Type', render: (r) => <>{r.snapshotType}</> },
    { key: 'size', header: 'Size', render: (r) => <>{formatBytes(r.sizeBytes)}</> },
    {
      key: 'retentionClass',
      header: 'Retention',
      render: (r) => <StatusBadge status="info" label={r.retentionClass} />,
    },
    { key: 'expiresAt', header: 'Expires', render: (r) => <>{new Date(r.expiresAt).toLocaleDateString()}</> },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <button style={btnPrimary} onClick={() => setRestoreDialogSnapshot(r)}>
          Restore
        </button>
      ),
    },
  ]

  if (error) {
    return (
      <div>
        <PageHeader title="Backup Dashboard" />
        <p style={{ color: colors.danger }}>{error}</p>
      </div>
    )
  }

  return (
    <div>
      <PageHeader title="Backup Dashboard" />

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <SummaryCard label="Total Environments" value={environments.length} />
        <SummaryCard
          label="Stale Backups (>36h)"
          value={staleCount}
          color={staleCount > 0 ? colors.danger : undefined}
        />
        <SummaryCard
          label="Active Restores"
          value={activeJobId && activeJob?.status !== 'completed' && activeJob?.status !== 'failed' ? 1 : 0}
        />
      </div>

      <DataTable
        columns={envColumns}
        rows={environments as EnvironmentBackupSummary[]}
        keyField="id"
        onRowClick={(row: EnvironmentBackupSummary) => setExpandedEnvId(expandedEnvId === row.id ? null : row.id)}
        emptyMessage="No environments with backup configured"
      />

      {expandedEnvId && (
        <div style={{ marginTop: '1rem', padding: '1rem', background: colors.bgCard, borderRadius: 8 }}>
          <h3 style={{ margin: '0 0 0.5rem' }}>
            Snapshots for {environments.find((e) => e.id === expandedEnvId)?.name}
          </h3>
          <DataTable
            columns={snapshotColumns}
            rows={snapshots as BackupSnapshot[]}
            keyField="id"
            emptyMessage="No snapshots"
          />
        </div>
      )}

      {restoreDialogSnapshot && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setRestoreDialogSnapshot(null)}
        >
          <div
            style={{ background: colors.bgCard, padding: '2rem', borderRadius: 8, minWidth: 400 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Confirm Restore</h3>
            <p>
              Restore snapshot from <strong>{new Date(restoreDialogSnapshot.uploadedAt).toLocaleString()}</strong> (
              {formatBytes(restoreDialogSnapshot.sizeBytes)})?
            </p>
            {environments.find((e) => e.id === restoreDialogSnapshot.environmentId)?.backupPassphraseEnabled && (
              <div style={{ marginTop: '1rem' }}>
                <label>Passphrase:</label>
                <input
                  type="password"
                  value={restorePassphrase}
                  onChange={(e) => setRestorePassphrase(e.target.value)}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: '0.5rem' }}
                />
              </div>
            )}
            {restoreError && <p style={{ color: colors.danger }}>{restoreError}</p>}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setRestoreDialogSnapshot(null)}>Cancel</button>
              <button style={btnPrimary} onClick={handleRestore} disabled={restoring}>
                {restoring ? 'Restoring...' : 'Restore'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeJob && (
        <div style={{ marginTop: '1rem', padding: '1rem', background: colors.bgCard, borderRadius: 8 }}>
          <h4>Restore Job: {activeJob.id.slice(0, 8)}</h4>
          <StatusBadge
            status={activeJob.status === 'completed' ? 'active' : activeJob.status === 'failed' ? 'expired' : 'warning'}
            label={activeJob.status}
          />
          {activeJob.errorMessage && <p style={{ color: colors.danger }}>{activeJob.errorMessage}</p>}
        </div>
      )}
    </div>
  )
}

function SummaryCard({
  label,
  value,
  color,
}: {
  readonly label: string
  readonly value: number
  readonly color?: string
}) {
  return (
    <div
      style={{ padding: '1rem 1.5rem', background: colors.bgCard, borderRadius: 8, minWidth: 150, textAlign: 'center' }}
    >
      <div style={{ fontSize: '2rem', fontWeight: 700, color: color ?? colors.text }}>{value}</div>
      <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>{label}</div>
    </div>
  )
}
